import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Container, Box, VStack, Heading, Input, Button, Text } from '@chakra-ui/react';
import { authService } from '@/services/firebase/authService';
import { LoginData } from '@/types/user';
import { useAuthStore } from '@/stores/authStore';

/**
 * ログインページコンポーネント
 */
const LoginPage: FC = () => {
  const navigate = useNavigate();
  const { setUser, setFirebaseUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginData>();

  /**
   * ログインフォーム送信処理
   */
  const onSubmit = async (data: LoginData) => {
    setIsLoading(true);
    setError(null);

    try {
      const user = await authService.login(data);
      const firebaseUser = authService.getCurrentUser();

      setUser(user);
      setFirebaseUser(firebaseUser);

      navigate('/dogs');
    } catch (err) {
      console.error('ログインエラー:', err);
      setError(err instanceof Error ? err.message : 'ログインに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="md" py={20}>
      <VStack gap={8}>
        <Heading size="2xl" textAlign="center">
          ブリーダー管理システム
        </Heading>

        <Box w="100%" bg="white" p={8} borderRadius="lg" shadow="md">
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack gap={4} align="stretch">
              {/* エラーメッセージ */}
              {error && (
                <Box p={3} bg="red.50" borderRadius="md" color="red.700">
                  <Text fontSize="sm">{error}</Text>
                </Box>
              )}

              {/* メールアドレス */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  メールアドレス
                </Text>
                <Input
                  type="email"
                  placeholder="example@example.com"
                  {...register('email', {
                    required: 'メールアドレスを入力してください',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: '有効なメールアドレスを入力してください',
                    },
                  })}
                />
                {errors.email && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    {errors.email.message}
                  </Text>
                )}
              </Box>

              {/* パスワード */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  パスワード
                </Text>
                <Input
                  type="password"
                  placeholder="8文字以上"
                  {...register('password', {
                    required: 'パスワードを入力してください',
                    minLength: {
                      value: 8,
                      message: 'パスワードは8文字以上で入力してください',
                    },
                  })}
                />
                {errors.password && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    {errors.password.message}
                  </Text>
                )}
              </Box>

              {/* ログインボタン */}
              <Button type="submit" colorPalette="blue" w="100%" loading={isLoading}>
                {isLoading ? 'ログイン中...' : 'ログイン'}
              </Button>
            </VStack>
          </form>

          <VStack mt={4} gap={2}>
            <Text fontSize="sm" color="gray.600">
              パスワードをお忘れの方は管理者にお問い合わせください
            </Text>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
};

export default LoginPage;
